import { onMounted, onUnmounted } from 'vue'

import { TOKEN_UPDATED_EVENT, getStoredToken, hasRequiredScope, refreshToken } from '@/services/auth'

// A tab that has been hidden longer than this stops refreshing, so a forgotten tab eventually logs out.
const ACTIVE_TIMEOUT_MS = Number(import.meta.env.VITE_ACTIVE_TIMEOUT_MS) || 1 * 60 * 60 * 1000

export const useTokenRefresh = () => {
  let refreshTimeout: number | undefined
  let hiddenAt = 0

  const clearRefreshTimeout = () => {
    if (refreshTimeout) {
      clearTimeout(refreshTimeout)
      refreshTimeout = undefined
    }
  }

  const tokenSecondsLeft = () => {
    const token = getStoredToken()
    if (!token?.payload?.exp) {
      return null
    }
    return token.payload.exp - Date.now() / 1000
  }

  const scheduleNextRefresh = () => {
    clearRefreshTimeout()

    const hasStaffScope = hasRequiredScope('broker')
    const ttlSeconds = tokenSecondsLeft()
    if (!hasStaffScope || ttlSeconds === null || ttlSeconds <= 1) {
      return
    }

    const waitSeconds = ttlSeconds / 2 + Math.random() * (ttlSeconds / 3)
    refreshTimeout = window.setTimeout(() => {
      void refreshTokenTimeout()
    }, waitSeconds * 1000)
  }

  const refreshTokenTimeout = async () => {
    const ttlSeconds = tokenSecondsLeft()
    if (ttlSeconds === null) {
      clearRefreshTimeout()
      return
    }

    const now = Date.now()
    const isActive = hiddenAt === 0 || now - hiddenAt < ACTIVE_TIMEOUT_MS
    if (ttlSeconds > 1 && isActive) {
      try {
        await refreshToken()
      } catch {
        // Auth watcher handles invalid/missing sessions and redirects to login.
      }
    }

    scheduleNextRefresh()
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now()
      return
    }

    hiddenAt = 0
    scheduleNextRefresh()
  }

  const handleStorage = () => {
    scheduleNextRefresh()
  }

  onMounted(() => {
    window.addEventListener('storage', handleStorage)
    window.addEventListener(TOKEN_UPDATED_EVENT, handleStorage)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    scheduleNextRefresh()
  })

  onUnmounted(() => {
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener(TOKEN_UPDATED_EVENT, handleStorage)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    clearRefreshTimeout()
  })
}
